import { useEffect } from "react";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import quarterOfYear from "dayjs/plugin/quarterOfYear";

import { Grid, Typography, InputAdornment, Chip, Stack } from "@mui/material";

import { TextField, ToggleButtons } from "@aclymatepackages/atoms";
import {
  formatDollars,
  formatDecimal
} from "@aclymatepackages/formatters";
import { numbersRegExpTest } from "@aclymatepackages/reg-exp";

import GrayPaper from "../../atoms/GrayPaper";
import DatePicker from "../../atoms/mui/DatePicker";

import usePortfoliosContext from "../../../helpers/contexts/offsetsPortfolios";
import usePurchasingContext from "../../../helpers/contexts/purchasing";

dayjs.extend(advancedFormat);
dayjs.extend(quarterOfYear);

const FREQUENCY_OPTIONS = [
  { name: "Monthly", value: "monthly" },
  { name: "Quarterly", value: "quarterly" },
  { name: "Yearly", value: "yearly" }
];

const FREQUENCY_LABEL_MAP = {
  monthly: "month",
  quarterly: "quarter",
  yearly: "year"
};

const FREQUENCY_MONTHS_MAP = {
  monthly: 1,
  quarterly: 3,
  yearly: 12
};

const defaultDateForFrequency = (frequency) =>
  dayjs()
    .add(FREQUENCY_MONTHS_MAP[frequency], "month")
    .startOf("day")
    .toDate();

const nextOccurrence = (dayJsValue) =>
  dayJsValue.isAfter(dayjs())
    ? dayJsValue.toDate()
    : dayJsValue.add(1, "year").toDate();

const buildPresetsForFrequency = (frequency) => {
  if (frequency === "monthly") {
    const nextMonth = dayjs().add(1, "month");
    return [
      {
        label: "1st of next month",
        date: nextMonth.startOf("month").toDate()
      },
      {
        label: "15th of next month",
        date: nextMonth.startOf("month").date(15).toDate()
      },
      {
        label: "Last day of next month",
        date: nextMonth.endOf("month").startOf("day").toDate()
      }
    ];
  }

  if (frequency === "quarterly") {
    const thisQuarterEnd = dayjs().endOf("quarter").startOf("day");
    const calendarQuarterEnd = thisQuarterEnd.isAfter(dayjs())
      ? thisQuarterEnd.toDate()
      : dayjs()
          .add(1, "quarter")
          .endOf("quarter")
          .startOf("day")
          .toDate();
    return [
      { label: "End of next calendar quarter", date: calendarQuarterEnd },
      {
        label: "Start of next quarter",
        date: dayjs().add(1, "quarter").startOf("quarter").toDate()
      },
      {
        label: "Today + 3 months",
        date: defaultDateForFrequency("quarterly")
      }
    ];
  }

  const dec31 = dayjs().month(11).date(31).startOf("day");
  const jun30 = dayjs().month(5).date(30).startOf("day");
  return [
    { label: `December 31, ${dayjs(nextOccurrence(dec31)).year()}`, date: nextOccurrence(dec31) },
    { label: `June 30, ${dayjs(nextOccurrence(jun30)).year()} (fiscal year-end)`, date: nextOccurrence(jun30) },
    { label: "Today + 1 year", date: defaultDateForFrequency("yearly") }
  ];
};

const isSameDay = (a, b) =>
  a && b && dayjs(a).isSame(dayjs(b), "day");

const formatAnchorPreview = (firstChargeDate, frequency) => {
  if (!firstChargeDate) {
    return null;
  }
  const anchor = dayjs(firstChargeDate);
  const day = anchor.format("Do");
  if (frequency === "monthly") {
    return `Your charges will fall on the ${day} of each month (clamped to the last day in shorter months).`;
  }
  if (frequency === "quarterly") {
    return `Your charges will fall every 3 months on the ${day} (clamped to month-end as needed).`;
  }
  return `Your charges will fall once a year on ${anchor.format("MMMM Do")}.`;
};

const SubscriptionConfigForm = () => {
  const { selectedPortfolio } = usePortfoliosContext();
  const { displayPricePerTon, name: portfolioName } = selectedPortfolio || {};

  const {
    budgetDollars,
    setBudgetDollars,
    frequency,
    setFrequency,
    firstChargeDate,
    setFirstChargeDate,
    setInputError
  } = usePurchasingContext();

  useEffect(() => {
    if (!firstChargeDate) {
      setFirstChargeDate(defaultDateForFrequency(frequency));
    }
  }, [firstChargeDate, frequency, setFirstChargeDate]);

  const handleFrequencyChange = (newFrequency) => {
    setFrequency(newFrequency);
    setFirstChargeDate(defaultDateForFrequency(newFrequency));
  };

  const handleBudgetChange = (rawValue) => {
    if (!numbersRegExpTest(rawValue)) {
      setInputError(true);
      return setBudgetDollars(0);
    }
    setInputError(false);
    return setBudgetDollars(Number(rawValue));
  };

  const handleDatePickerChange = (newDate) => {
    if (!newDate) {
      return setFirstChargeDate(defaultDateForFrequency(frequency));
    }
    return setFirstChargeDate(dayjs(newDate).startOf("day").toDate());
  };

  const tonsEstimate =
    budgetDollars && displayPricePerTon
      ? budgetDollars / displayPricePerTon
      : 0;

  const frequencyLabel = FREQUENCY_LABEL_MAP[frequency];
  const presets = buildPresetsForFrequency(frequency);
  const previewLine = formatAnchorPreview(firstChargeDate, frequency);

  const minDate = dayjs().add(1, "day").startOf("day").toDate();
  const maxDate = dayjs()
    .add(FREQUENCY_MONTHS_MAP[frequency] * 2, "month")
    .endOf("day")
    .toDate();

  return (
    <GrayPaper p={2}>
      <Grid container direction="column" spacing={2}>
        <Grid item>
          <Typography variant="h5" color="textPrimary">
            Configure your subscription
          </Typography>
          <Typography variant="body2" color="textSecondary">
            We will automatically purchase offsets from the {portfolioName}{" "}
            portfolio on the schedule you choose, up to your budget.
          </Typography>
        </Grid>
        <Grid item>
          <ToggleButtons
            value={frequency}
            onChange={handleFrequencyChange}
            buttons={FREQUENCY_OPTIONS}
          />
        </Grid>
        <Grid item>
          <TextField
            value={budgetDollars ? String(budgetDollars) : ""}
            setValue={handleBudgetChange}
            label={`Budget per ${frequencyLabel}`}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">$</InputAdornment>
              )
            }}
            error={budgetDollars !== 0 && !numbersRegExpTest(budgetDollars)}
          />
        </Grid>
        <Grid item>
          <Typography variant="subtitle2" color="textPrimary">
            First charge date
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Pick when the first cycle should run. Subsequent cycles fall on the
            same day, every {frequencyLabel}.
          </Typography>
        </Grid>
        <Grid item>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {presets.map(({ label, date }) => (
              <Chip
                key={label}
                label={label}
                clickable
                color={isSameDay(firstChargeDate, date) ? "secondary" : "default"}
                variant={
                  isSameDay(firstChargeDate, date) ? "filled" : "outlined"
                }
                onClick={() => setFirstChargeDate(date)}
              />
            ))}
          </Stack>
        </Grid>
        <Grid item>
          <DatePicker
            date={firstChargeDate}
            editDate={handleDatePickerChange}
            minDate={minDate}
            maxDate={maxDate}
            label="First charge date"
          />
        </Grid>
        {previewLine ? (
          <Grid item>
            <Typography variant="caption" color="textSecondary">
              {previewLine}
            </Typography>
          </Grid>
        ) : null}
        {budgetDollars > 0 && displayPricePerTon ? (
          <Grid item>
            <Typography variant="subtitle2" color="textSecondary">
              At today's price of {formatDollars(displayPricePerTon)}/ton, each
              cycle will buy approximately{" "}
              <strong>{formatDecimal(tonsEstimate)} tons</strong> for{" "}
              <strong>{formatDollars(budgetDollars)}</strong>.
            </Typography>
          </Grid>
        ) : null}
      </Grid>
    </GrayPaper>
  );
};

export default SubscriptionConfigForm;
